import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  Grid,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import { MuiTelInput } from "mui-tel-input";
import { SERVER_BASE_URL } from "../Config";

interface ProviderInfo {
  email: string;
  name: string;
  phone: string;
  npi: string;
}

interface Address {
  id: number | null;
  street: string;
  city: string;
  state: string;
  zip: string;
  isDefault: boolean;
}

const ProviderForm: React.FC = () => {
  const [providerName, setProviderName] = useState<string>("");
  const [providerEmail, setProviderEmail] = useState<string>("");
  const [providerPhone, setProviderPhone] = useState<string>("");
  const [providerNpi, setProviderNpi] = useState<string>("");
  const [providerPracticeName, setProviderPracticeName] = useState<string>("");
  const [providerStateLicenses, setProviderStateLicenses] = useState<string>("");
  const [providerMobilePhone, setProviderMobilePhone] = useState<string>("");
  const [providerFax, setProviderFax] = useState<string>("");
  const [providerSalesRep, setProviderSalesRep] = useState<string>("");
  const [providerContact, setProviderContact] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showAddressListModal, setShowAddressListModal] = useState(false);
  const [showAddressEditModal, setShowAddressEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<Address>({
    id: null,
    street: "",
    city: "",
    state: "",
    zip: "",
    isDefault: false
  });

  const handleSave = async () => {
    if (!providerNpi || !providerNpi.trim()) {
      alert("NPI is required.");
      return;
    }
    const providerInfo = {
      name: providerName,
      phone: providerPhone,
      npi: parseInt(providerNpi),
      practice_name: providerPracticeName,
      state_licenses: providerStateLicenses,
      mobile_phone: providerMobilePhone,
      fax: providerFax,
      sales_rep: providerSalesRep,
      contact: providerContact,
    };

    const providerToken = localStorage.getItem("providerToken");
    try {
      const response = await fetch(`${SERVER_BASE_URL}/provider`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`,
        },
        body: JSON.stringify(providerInfo),
      });
    } catch (error) {
      console.error("Failed to save provider data:", error);
    }

    setEditMode(false);
  };

  const toggleEditMode = () => {
    if (editMode) {
      handleSave();
    }
    setEditMode((prev) => !prev);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProviderName(event.target.value);
  };

  const handlePhoneChange = (value: string) => {
    if (value !== providerPhone) setProviderPhone(value);
  };

  const loadProvider = async () => {
    const providerToken = localStorage.getItem("providerToken");
    try {
      const response = await fetch(`${SERVER_BASE_URL}/provider`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`,
        },
      });
      const data = await response.json();
      setProviderEmail(data.email);
      setProviderName(data.name);
      setProviderPhone(data.phone);
      setProviderNpi(data.npi ? String(data.npi) : "");
      setProviderPracticeName(data.practiceName || "");
      setProviderStateLicenses(data.stateLicenses || "");
      setProviderMobilePhone(data.mobilePhone || "");
      setProviderFax(data.fax || "");
      setProviderSalesRep(data.salesRep || "");
      setProviderContact(data.contact || "");
    } catch (error) {
      console.error("Failed to load provider data:", error);
    }
  };

  const fetchAddresses = async () => {
    const providerToken = localStorage.getItem("providerToken");
    try {
      const response = await fetch(`${SERVER_BASE_URL}/provider_address`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch addresses");
      }
      const data = await response.json();
      setAddresses(data);
    } catch (err) {
      console.error("Error fetching addresses:", err);
    }
  };

  const handleNewAddressClick = () => {
    setFormData({
      id: null,
      street: "",
      city: "",
      state: "",
      zip: "",
      isDefault: addresses.length === 0
    });
    setIsEditing(false);
    setShowAddressEditModal(true);
  };

  const handleEditAddressClick = (address: Address) => {
    setFormData(address);
    setIsEditing(true);
    setShowAddressEditModal(true);
  };

  const handleSaveAddress = async () => {
    const providerToken = localStorage.getItem("providerToken");
    try {
      if (isEditing && formData.id !== null) {
        const response = await fetch(`${SERVER_BASE_URL}/provider_address/${formData.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${providerToken}`
          },
          body: JSON.stringify({
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            makeDefault: formData.isDefault
          })
        });
        if (!response.ok) {
          throw new Error("Failed to update address");
        }
        // If this address is now default, update all others
        if (formData.isDefault) {
          setAddresses((prevAddresses) =>
            prevAddresses.map((addr) => ({
              ...addr,
              isDefault: addr.id === formData.id
            }))
          );
        } else {
          setAddresses((prevAddresses) =>
            prevAddresses.map((addr) =>
              addr.id === formData.id ? { ...formData } : addr
            )
          );
        }
      } else {
        const response = await fetch(`${SERVER_BASE_URL}/provider_address`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${providerToken}`
          },
          body: JSON.stringify({
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            makeDefault: formData.isDefault
          })
        });
        if (!response.ok) {
          throw new Error("Failed to save address");
        }
        const data = await response.json();
        const newAddress = { ...formData, id: data.address_id, isDefault: data.isDefault };
        // If new address is default, update others
        if (data.isDefault) {
          setAddresses((prevAddresses) => [
            ...prevAddresses.map(addr => ({ ...addr, isDefault: false })),
            newAddress
          ]);
        } else {
          setAddresses((prevAddresses) => [...prevAddresses, newAddress]);
        }
      }
      setShowAddressEditModal(false);
    } catch (error) {
      console.error("Error saving address:", error);
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }
    const providerToken = localStorage.getItem("providerToken");
    try {
      const response = await fetch(`${SERVER_BASE_URL}/provider_address/${addressId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to delete address");
      }
      // Refresh addresses to get updated default
      fetchAddresses();
    } catch (error) {
      console.error("Error deleting address:", error);
    }
  };

  useEffect(() => {
    Promise.all([loadProvider(), fetchAddresses()]).finally(() => setLoading(false));
  }, []);

  const fieldRow = (label: string, value: string) => (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, py: 1.5, borderBottom: "1px solid #eee" }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: { xs: "100%", sm: 160 }, flexShrink: 0, textAlign: "left" }}>{label}</Typography>
      <Typography variant="body1">{value || "—"}</Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {providerEmail}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Name" variant="outlined" fullWidth onChange={handleNameChange} value={providerName} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="NPI" variant="outlined" fullWidth value={providerNpi} onChange={(e) => setProviderNpi(e.target.value)} inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Clinic Name" variant="outlined" fullWidth value={providerPracticeName} onChange={(e) => setProviderPracticeName(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="State Licenses" variant="outlined" fullWidth value={providerStateLicenses} onChange={(e) => setProviderStateLicenses(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput label="Office Phone" fullWidth value={providerPhone} defaultCountry={"US"} disableDropdown onChange={handlePhoneChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput label="Mobile Phone" fullWidth value={providerMobilePhone} defaultCountry={"US"} disableDropdown onChange={(value) => { if (value !== providerMobilePhone) setProviderMobilePhone(value); }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Fax" variant="outlined" fullWidth value={providerFax} onChange={(e) => setProviderFax(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Sales Rep" variant="outlined" fullWidth value={providerSalesRep} onChange={(e) => setProviderSalesRep(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Additional Point of Contact Information" variant="outlined" fullWidth multiline minRows={3} value={providerContact} onChange={(e) => setProviderContact(e.target.value)} />
          </Grid>
        </Grid>
      ) : (
        <Box>
          {fieldRow("Email", providerEmail)}
          {fieldRow("Name", providerName)}
          {fieldRow("NPI", providerNpi)}
          {fieldRow("Clinic Name", providerPracticeName)}
          {fieldRow("State Licenses", providerStateLicenses)}
          {fieldRow("Office Phone", providerPhone)}
          {fieldRow("Mobile Phone", providerMobilePhone)}
          {fieldRow("Fax", providerFax)}
          {fieldRow("Sales Rep", providerSalesRep)}
          {fieldRow("Additional Point of Contact Information", providerContact)}
        </Box>
      )}

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" color="primary" onClick={toggleEditMode}>
          {editMode ? "Save" : "Edit"}
        </Button>
        <Button variant="outlined" color="primary" onClick={() => setShowAddressListModal(true)}>
          Manage Addresses
        </Button>
      </Box>

      {/* Address List Modal */}
      <Modal
        open={showAddressListModal}
        onClose={() => setShowAddressListModal(false)}
        aria-labelledby="address-list-modal"
        aria-describedby="modal-to-list-addresses"
        sx={{ zIndex: 1400 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            onClick={() => setShowAddressListModal(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            Manage Addresses
          </Typography>
          {addresses.map((address) => (
            <Card key={address.id} sx={{ mb: 2 }}>
              <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {`${address.street}, ${address.city}, ${address.state} ${address.zip}`}
                  </Typography>
                  {address.isDefault && (
                    <Chip label="Default" size="small" color="primary" />
                  )}
                </Box>
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                  <IconButton onClick={() => handleEditAddressClick(address)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => address.id && handleDeleteAddress(address.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewAddressClick}
            sx={{ mt: 2 }}
          >
            New Address
          </Button>
        </Box>
      </Modal>

      {/* Address Edit/Create Modal */}
      <Modal
        open={showAddressEditModal}
        onClose={() => setShowAddressEditModal(false)}
        aria-labelledby="address-edit-modal"
        aria-describedby="modal-to-create-or-edit-address"
        sx={{ zIndex: 1500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            onClick={() => setShowAddressEditModal(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {isEditing ? "Edit Address" : "Create New Address"}
          </Typography>
          <TextField
            label="Street"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.street}
            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
          />
          <TextField
            label="City"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
          <TextField
            label="State"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          />
          <TextField
            label="ZIP"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.zip}
            onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              />
            }
            label="Make this the default address"
            sx={{ mb: 2 }}
          />
          <Button variant="contained" color="primary" onClick={handleSaveAddress}>
            {isEditing ? "Update Address" : "Save Address"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProviderForm;
